import { useState, useEffect } from 'react';
import { X, MapPin, Phone, Mail, Calendar, Lock } from 'lucide-react';
import { apiUrl } from '../utils/api';

interface Campaign {
  id: string;
  title: string;
  description: string;
  deadline: string;
  location: { city: string; state: string; address?: string; country?: string };
  privacyMode: 'public' | 'private';
  status: string;
  contactInfo?: { phone: string; email: string };
  isVerified?: boolean;
}

interface EditCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  campaign: Campaign | null;
}

export function EditCampaignModal({ isOpen, onClose, onSuccess, campaign }: EditCampaignModalProps) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    deadline: '',
    city: '',
    state: '',
    address: '',
    phone: '',
    email: '',
    privacyMode: 'private' as 'public' | 'private',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Track original values to only send changes
  const [original, setOriginal] = useState(formData);

  useEffect(() => {
    if (campaign && isOpen) {
      const data = {
        title: campaign.title || '',
        description: campaign.description || '',
        deadline: campaign.deadline ? new Date(campaign.deadline).toLocaleDateString('en-CA') : '',
        city: campaign.location?.city || '',
        state: campaign.location?.state || '',
        address: (campaign.location as any)?.address || '',
        phone: campaign.contactInfo?.phone || '',
        email: campaign.contactInfo?.email || '',
        privacyMode: campaign.privacyMode || 'private',
      };
      setFormData(data);
      setOriginal(data);
      setError('');
    }
  }, [campaign, isOpen]);

  if (!isOpen || !campaign) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const getChangedFields = () => {
    const changes: any = {};
    
    if (formData.title !== original.title) changes.title = formData.title;
    if (formData.description !== original.description) changes.description = formData.description;
    if (formData.deadline !== original.deadline) changes.deadline = new Date(formData.deadline + 'T23:59:59').toISOString();
    if (formData.privacyMode !== original.privacyMode) changes.privacyMode = formData.privacyMode;

    // Location changes
    if (formData.city !== original.city || formData.state !== original.state || formData.address !== original.address) {
      changes.location = {
        country: (campaign.location as any)?.country || 'Colombia',
        state: formData.state || formData.city,
        city: formData.city,
        address: formData.address,
      };
    }

    // Contact changes
    if (formData.phone !== original.phone || formData.email !== original.email) {
      changes.contactInfo = {
        phone: formData.phone,
        email: formData.email,
      };
    }

    return changes;
  };

  const handleSubmit = async () => {
    const changes = getChangedFields();

    if (Object.keys(changes).length === 0) {
      onClose();
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(apiUrl(`/api/campaigns/${campaign.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(changes),
      });

      const result = await response.json();

      if (result.success) {
        onSuccess();
        onClose();
      } else {
        setError(result.error?.message || 'Error al actualizar');
      }
    } catch {
      setError('Error de conexión');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-xl z-10">
            <h2 className="text-lg font-semibold text-gray-900">Editar Campaña</h2>
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-6 py-5 space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">{error}</div>
            )}

            {/* Cover image */}
            {/* Removed — use separate Cover Image modal from campaign card */}

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Título</label>
              <input
                name="title" value={formData.title} onChange={handleChange}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
              <textarea
                name="description" value={formData.description} onChange={handleChange} rows={3}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Deadline */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" /> Fecha límite
              </label>
              <input
                type="date" name="deadline" value={formData.deadline} onChange={handleChange}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Privacy / Visibility */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Visibilidad</label>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                {campaign.isVerified ? (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <Lock className="w-4 h-4 text-indigo-600" />
                        <span className="text-sm font-medium text-gray-900">
                          {formData.privacyMode === 'public' ? 'Pública' : 'Privada'}
                        </span>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={formData.privacyMode === 'public'}
                        onClick={() => {
                          setFormData({ ...formData, privacyMode: formData.privacyMode === 'public' ? 'private' : 'public' });
                          if (error) setError('');
                        }}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${formData.privacyMode === 'public' ? 'bg-indigo-600' : 'bg-gray-300'}`}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${formData.privacyMode === 'public' ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
                      </button>
                    </div>
                    <p className="text-xs text-gray-600">
                      {formData.privacyMode === 'public'
                        ? 'Tu campaña es visible en el marketplace público.'
                        : 'Tu campaña es privada — solo accesible por enlace directo.'}
                    </p>
                  </>
                ) : (
                  <>
                    <div className="flex items-center space-x-2 mb-2">
                      <Lock className="w-4 h-4 text-indigo-600" />
                      <span className="text-sm font-medium text-gray-900">Privada</span>
                    </div>
                    <p className="text-xs text-gray-600 mb-2">
                      Tu campaña es privada — solo accesible por enlace directo. Comparte tu link por WhatsApp o redes sociales con las personas que quieras que participen.
                    </p>
                    <p className="text-xs text-gray-500 mb-2">
                      Si representas una organización con registro legal, puedes solicitar verificación para aparecer en el marketplace público.
                    </p>
                    <a
                      href={`https://wa.me/573169277199?text=${encodeURIComponent('Hola, quiero solicitar verificación para mi campaña en DonaApp. Mi correo registrado es: ')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
                    >
                      Solicitar verificación →
                    </a>
                  </>
                )}
              </div>
            </div>

            {/* Location */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <MapPin className="w-4 h-4 inline mr-1" /> Ciudad
                </label>
                <input
                  name="city" value={formData.city} onChange={handleChange}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Departamento</label>
                <input
                  name="state" value={formData.state} onChange={handleChange}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
              <input
                name="address" value={formData.address} onChange={handleChange}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Contact */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Phone className="w-4 h-4 inline mr-1" /> Teléfono
                </label>
                <input
                  name="phone" value={formData.phone} onChange={handleChange}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Mail className="w-4 h-4 inline mr-1" /> Email
                </label>
                <input
                  name="email" value={formData.email} onChange={handleChange}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-3 rounded-b-xl">
            <button onClick={onClose} className="px-4 py-2.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="px-5 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {isLoading ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
